import { MappingTypeMapping } from '@elastic/elasticsearch/lib/api/types';
import { zCustom } from '@nucleus/lib-shape';
import { merge } from 'lodash';
import * as yup from 'yup';
import { z } from 'zod';
import { PublishableEntity, PublishableEntitySchemaDeprecated } from './PublishableEntity';
import { PublishableMetadata, PublishableMetadataSchemaDeprecated } from './PublishableMetadata';
import { PublishablePrivacyControlsSchema } from './PublishablePrivacy';
import {
  PublishableRevision,
  PublishableRevisionSchema,
  PublishableRevisionSchemaDeprecated,
} from './PublishableRevision';
import { PublishableVersionLock, PublishableVersionLockSchemaDeprecated } from './PublishableVersion';
import { BaseRoutedIndexDocumentMapping, BaseRoutedIndexDocumentSchema } from './elasticsearch';

export interface Publishable<
  R extends PublishableRevision = PublishableRevision,
  E extends PublishableEntity = PublishableEntity,
> {
  id: E['id'];
  entity: E;
  revision: R;
  lock?: PublishableVersionLock;
  metadata: PublishableMetadata;
}

/**
 * @deprecated this schema is deprecated and will be removed in the future
 */
export const PublishableSchemaDeprecated: yup.SchemaOf<Publishable> = yup.object({
  id: PublishableEntitySchemaDeprecated.fields['id'].required(),
  entity: PublishableEntitySchemaDeprecated.required(),
  revision: PublishableRevisionSchemaDeprecated.required(),
  lock: PublishableVersionLockSchemaDeprecated.optional().default(undefined),
  metadata: PublishableMetadataSchemaDeprecated.required(),
});

export type PublishableIndexDocument = z.infer<typeof PublishableIndexDocumentSchema>;

export const PublishableIndexDocumentSchema = BaseRoutedIndexDocumentSchema.extend({
  engineId: z.string(),
  excludeFromListing: z.boolean().optional(),
  noindex: z.boolean().optional(),
  title: z.string().optional(),
  slug: z.string(),
  revisionId: z.string(),
  revisionIndex: z.number().min(1),
  revisionType: PublishableRevisionSchema.shape.revisionType,
  privacy: PublishablePrivacyControlsSchema,
  archivedAt: zCustom.iso().optional(),
  archivedBy: z.string().optional(),
  publishedAt: zCustom.iso().optional(),
  publishedBy: z.string().optional(),
  unpublishedAt: zCustom.iso().optional(),
  unpublishedBy: z.string().optional(),
  updatedAt: zCustom.iso().optional(),
  updatedBy: z.string().optional(),
  createdAt: zCustom.iso(),
  createdBy: z.string(),
  latestRevisionAt: zCustom.iso(),
  latestRevisionBy: z.string(),
  latestRevisionIndex: z.number().min(1),
  latestRevisionPublishedAt: zCustom.iso().optional(),
  latestRevisionPublishedBy: z.string().optional(),
  latestRevisionPublishedIndex: z.number().min(1).optional(),
});

export const PublishableIndexDocumentMapping: MappingTypeMapping = merge({}, BaseRoutedIndexDocumentMapping, {
  properties: {
    engineId: {
      type: 'keyword',
    },
    slug: {
      type: 'keyword',
    },
    title: {
      type: 'text',
      fields: { keyword: { type: 'keyword', ignore_above: 256 } },
    },
    excludeFromListing: { type: 'boolean' },
    noindex: { type: 'boolean' },
    privacy: {
      type: 'object',
      properties: {
        active: { type: 'boolean' },
        criteria: {
          type: 'object',
          properties: {
            connectionType: {
              type: 'keyword',
            },
          },
        },
      },
    },

    createdAt: { type: 'date' },
    createdBy: { type: 'keyword' },
    publishedAt: { type: 'date' },
    publishedBy: { type: 'keyword' },
    unpublishedAt: { type: 'date' },
    unpublishedBy: { type: 'keyword' },

    updatedAt: { type: 'date' },
    updatedBy: { type: 'keyword' },
    archivedAt: { type: 'date' },
    archivedBy: { type: 'keyword' },
    latestRevisionAt: { type: 'date' },
    latestRevisionBy: { type: 'keyword' },
    latestRevisionIndex: { type: 'integer' },
    latestRevisionPublishedAt: { type: 'date' },
    latestRevisionPublishedBy: { type: 'keyword' },
    latestRevisionPublishedIndex: { type: 'integer' },
  },
});
